import {
  Box, Card, CardContent, Typography, Table, TableBody, TableCell,
  TableHead, TableRow, Chip, Button, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, MenuItem, CircularProgress, Alert, IconButton,
  Snackbar, Stepper, Step, StepLabel,
} from '@mui/material';
import { Add, Edit } from '@mui/icons-material';
import { useState, useEffect } from 'react';
import api from '../../api/axios';

const STATUS_STEPS = ['planned', 'in_progress', 'completed'];
const statusColor = { planned: 'default', in_progress: 'info', completed: 'success', cancelled: 'error' };

const EMPTY = { order_number: '', product_name: '', quantity: '', notes: '', planned_date: '' };

function OrderDialog({ order, open, onClose, onSuccess }) {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const isEdit = !!order?.id;

  useEffect(() => {
    setForm(order ? {
      order_number: order.order_number || '',
      product_name: order.product_name || '',
      quantity: order.quantity || '',
      notes: order.notes || '',
      planned_date: order.planned_date ? order.planned_date.split('T')[0] : '',
    } : EMPTY);
    setError('');
  }, [order, open]);

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const handle = async () => {
    if (!form.product_name || !form.quantity) { setError('Product name and quantity are required.'); return; }
    setLoading(true); setError('');
    try {
      if (isEdit) await api.put(`/production/${order.id}`, form);
      else await api.post('/production', form);
      onSuccess();
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to save order');
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isEdit ? 'Edit Production Order' : 'New Production Order'}</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
        {error && <Alert severity="error">{error}</Alert>}
        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField label="Order #" value={form.order_number} onChange={set('order_number')} fullWidth placeholder="Auto-generated if blank" />
          <TextField label="Planned Date" type="date" value={form.planned_date} onChange={set('planned_date')} fullWidth InputLabelProps={{ shrink: true }} />
        </Box>
        <TextField label="Product / Item Name" value={form.product_name} onChange={set('product_name')} fullWidth required />
        <TextField label="Quantity" type="number" value={form.quantity} onChange={set('quantity')} fullWidth required inputProps={{ min: 1 }} />
        <TextField label="Notes / Instructions" value={form.notes} onChange={set('notes')} multiline rows={3} fullWidth />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handle} disabled={loading}>
          {loading ? <CircularProgress size={20} /> : isEdit ? 'Update' : 'Create Order'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function ProductionPage() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [snack, setSnack] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/production', { params: { status: statusFilter || undefined } });
      setOrders(data.data || []);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [statusFilter]);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/production/${id}/status`, { status });
      setSnack(`Order marked as ${status}`);
      load();
    } catch (e) { setSnack(e.response?.data?.message || 'Failed to update status'); }
  };

  const nextStatus = { planned: 'in_progress', in_progress: 'completed' };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" fontWeight={700}>Production Orders</Typography>
        <Button variant="contained" startIcon={<Add />} onClick={() => { setSelected(null); setDialogOpen(true); }}>New Order</Button>
      </Box>

      <Card sx={{ mb: 2 }}>
        <CardContent sx={{ pb: '16px !important' }}>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField select size="small" label="Status Filter" value={statusFilter} onChange={e => setStatusFilter(e.target.value)} sx={{ minWidth: 160 }}>
              <MenuItem value="">All</MenuItem>
              {['planned', 'in_progress', 'completed', 'cancelled'].map(s => (
                <MenuItem key={s} value={s}>{s.replace('_', ' ')}</MenuItem>
              ))}
            </TextField>
          </Box>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          {loading ? <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box> : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Order #</TableCell>
                  <TableCell>Product</TableCell>
                  <TableCell align="right">Qty</TableCell>
                  <TableCell>Planned Date</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Progress</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {orders.map(o => (
                  <TableRow key={o.id} hover>
                    <TableCell><Typography variant="body2" fontWeight={700} color="primary">{o.order_number || `#${o.id}`}</Typography></TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>{o.product_name}</Typography>
                      {o.notes && <Typography variant="caption" color="text.secondary">{o.notes}</Typography>}
                    </TableCell>
                    <TableCell align="right">{o.quantity}</TableCell>
                    <TableCell><Typography variant="caption">{o.planned_date ? new Date(o.planned_date).toLocaleDateString('en-PK') : '—'}</Typography></TableCell>
                    <TableCell><Chip label={o.status?.replace('_', ' ')} size="small" color={statusColor[o.status] || 'default'} /></TableCell>
                    <TableCell sx={{ minWidth: 200 }}>
                      {o.status !== 'cancelled' && (
                        <Stepper activeStep={STATUS_STEPS.indexOf(o.status)} sx={{ p: 0 }} size="small">
                          {STATUS_STEPS.map(s => <Step key={s}><StepLabel sx={{ '& .MuiStepLabel-label': { fontSize: '0.65rem' } }}>{s.replace('_', ' ')}</StepLabel></Step>)}
                        </Stepper>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                        <IconButton size="small" onClick={() => { setSelected(o); setDialogOpen(true); }}><Edit fontSize="small" /></IconButton>
                        {nextStatus[o.status] && (
                          <Button size="small" variant="outlined" onClick={() => updateStatus(o.id, nextStatus[o.status])}>
                            → {nextStatus[o.status]?.replace('_', ' ')}
                          </Button>
                        )}
                        {o.status !== 'cancelled' && o.status !== 'completed' && (
                          <Button size="small" color="error" onClick={() => updateStatus(o.id, 'cancelled')}>Cancel</Button>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
                {orders.length === 0 && (
                  <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary' }}>No production orders found</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <OrderDialog
        order={selected} open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSuccess={() => { setDialogOpen(false); setSnack(selected ? 'Order updated' : 'Order created'); load(); }}
      />
      <Snackbar open={!!snack} autoHideDuration={4000} onClose={() => setSnack('')} message={snack} />
    </Box>
  );
}
